import React from "react";

/**
 * The product form, defined once and used by both Add and Edit.
 *
 * It was previously two separate forms with different fields — Add offered
 * seven, Edit offered four — against a table with sixteen columns. That is how
 * `stock_count` ended up uneditable anywhere in the panel despite the store
 * decrementing it on every sale.
 *
 * One component, one field list, both callers. A column added here appears in
 * both places or neither.
 *
 * ── The pricing fields deserve an explanation ──
 *
 * The app resolves a product's price from `price_gbp` / `price_usd` and treats
 * a missing one as "not sold in that market" rather than converting — a live FX
 * rate drifts against the price the customer was shown, which turns into
 * rounding disputes at checkout.
 *
 * The old form only wrote the legacy `price` + `currency` pair, so every
 * product created through this panel was unbuyable in the UK, and in the US
 * fell back to `price` — charging $24.99 for a £24.99 product. Both native
 * prices are first-class fields now, and leaving one blank is a deliberate
 * "don't sell this here" rather than an oversight.
 */

export type ProductFormValues = {
  name: string;
  brand: string;
  description: string;
  how_to_use: string;
  price_gbp: string;
  price_usd: string;
  categories: string;
  sub_category: string;
  hair_types: string;
  concerns: string;
  in_stock: boolean;
  stock_count: string;
  store_link: string;
  /** fine | medium | coarse — mirrors the onboarding strand question. */
  strand_type: string[];
  ingredients: string;
};

/** Fixed set, so it stays matchable against the saved hair profile. */
export const STRAND_TYPES = ["fine", "medium", "coarse"] as const;

/** Tools have no formulation, so the ingredients field is hidden for them. */
export const hasIngredients = (categories: string) =>
  !/tools|accessor/i.test(categories);

/**
 * The categories the app and website actually filter on.
 *
 * This is free text in the database and the field stays free text — a product
 * can legitimately sit in more than one, and locking it to a dropdown would
 * make adding a category a code change. But a typo here doesn't fail: it
 * silently produces a product no category tile will ever show, which is how
 * "Growth" and "strength" ended up as one-product ghost categories that
 * migration 0016 had to clean up.
 *
 * Offered as a datalist and echoed in the hint, so the right spelling is one
 * keystroke away without forbidding anything else.
 *
 * Kept in step with CATEGORY_MAP in the app's constants/categories.ts.
 */
export const KNOWN_CATEGORIES = [
  "Hydrate/Moisture",
  "Repair",
  "Detox",
  "Strength",
  "Scalp Care",
  "Styling",
  "Tools and Accessories",
] as const;

/** Names a category that no tile in the app or website will match. */
export const unknownCategories = (value: string): string[] =>
  parseList(value).filter(
    (c) => !KNOWN_CATEGORIES.some((known) => known.toLowerCase() === c.toLowerCase())
  );

export const emptyProductForm: ProductFormValues = {
  name: "",
  brand: "",
  description: "",
  how_to_use: "",
  price_gbp: "",
  price_usd: "",
  categories: "",
  sub_category: "",
  hair_types: "",
  concerns: "",
  in_stock: true,
  stock_count: "0",
  store_link: "",
  strand_type: [],
  ingredients: "",
};

/** Comma-separated text → the text[] columns the app reads. */
export const parseList = (value: string): string[] =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const joinList = (value: unknown): string =>
  Array.isArray(value) ? value.join(", ") : "";

/** A database row → form values. The inverse of buildProductPayload. */
export function toFormValues(row: any): ProductFormValues {
  return {
    name: row.name ?? "",
    brand: row.brand ?? "",
    description: row.description ?? "",
    how_to_use: row.how_to_use ?? "",
    // Empty string rather than "0": a blank price means "not sold in this
    // market", and 0 would read as free.
    price_gbp: row.price_gbp != null ? String(row.price_gbp) : "",
    price_usd: row.price_usd != null ? String(row.price_usd) : "",
    categories: joinList(row.categories),
    sub_category: row.sub_category ?? "",
    hair_types: joinList(row.hair_types),
    concerns: joinList(row.concerns),
    in_stock: row.in_stock ?? true,
    stock_count: row.stock_count != null ? String(row.stock_count) : "0",
    store_link: row.store_link ?? "",
    strand_type: Array.isArray(row.strand_type) ? row.strand_type : [],
    ingredients: row.ingredients ?? "",
  };
}

/** Form values → the row shape, with the numeric coercion in one place. */
export function buildProductPayload(values: ProductFormValues, images: string[]) {
  const gbp = values.price_gbp.trim() ? Number(values.price_gbp) : null;
  const usd = values.price_usd.trim() ? Number(values.price_usd) : null;

  return {
    name: values.name.trim(),
    brand: values.brand.trim(),
    description: values.description.trim(),
    how_to_use: values.how_to_use.trim() || null,
    price_gbp: gbp,
    price_usd: usd,
    // `price` is the legacy column, it is NOT NULL, and the app reads it *only*
    // as a USD fallback — `getProductPrice` consults it when the shopper's
    // currency is USD, regardless of what the product's own `currency` says.
    //
    // So it must hold a USD figure or nothing. Writing the GBP amount here
    // when there is no USD price would show US shoppers a £ figure behind a $.
    // Zero is the correct "nothing": the fallback tests `> 0`, so it declines,
    // and the product reads as unavailable in the US — which is what a blank
    // USD price means.
    price: usd ?? 0,
    currency: "USD",
    categories: parseList(values.categories),
    sub_category: values.sub_category.trim() || null,
    hair_types: parseList(values.hair_types),
    concerns: parseList(values.concerns),
    in_stock: values.in_stock,
    stock_count: Number(values.stock_count) || 0,
    // Null rather than "" when blank: the app and website test this for
    // presence to decide whether to offer "visit site" at all, and an empty
    // string is truthy enough to slip through a careless check.
    store_link: values.store_link.trim() || null,
    strand_type: values.strand_type,
    // Cleared rather than stored for tools, so a product recategorised as an
    // accessory doesn't keep a stale ingredients list nothing will render.
    ingredients: hasIngredients(values.categories)
      ? values.ingredients.trim() || null
      : null,
    images,
  };
}

/** Returns the first problem with the form, or null when it is submittable. */
export function validateProduct(values: ProductFormValues): string | null {
  if (!values.name.trim()) return "Give the product a name";
  if (!values.brand.trim()) return "Give the product a brand";
  if (!values.price_gbp.trim() && !values.price_usd.trim()) {
    return "Set at least one price — a product with neither can't be bought in any market";
  }
  for (const [label, raw] of [
    ["GBP price", values.price_gbp],
    ["USD price", values.price_usd],
  ] as const) {
    if (raw.trim() && !(Number(raw) > 0)) return `${label} must be a number above zero`;
  }
  if (values.stock_count.trim() && Number(values.stock_count) < 0) {
    return "Stock count can't be negative";
  }
  // Mirrors the products_store_link_is_http constraint from migration 0014.
  // Without this the save fails at the database with a raw constraint error;
  // with it the admin gets told what's wrong before submitting. The database
  // remains the authority — this only improves the message.
  const link = values.store_link.trim();
  if (link) {
    let ok = false;
    try {
      const protocol = new URL(link).protocol;
      ok = protocol === "http:" || protocol === "https:";
    } catch {
      ok = false;
    }
    if (!ok) {
      return "Store link must be a full URL starting with http:// or https://";
    }
  }
  return null;
}

// ── Per-field auto-save (list item 18) ──────────────────────────────────────
//
// Editing writes each field as it loses focus rather than on a Save button.
// Two things make that safe:
//
//   • Only the touched column is written. Sending the whole row on every blur
//     would let a half-typed field elsewhere overwrite good data.
//   • A field that would fail validation is not written at all. Silent saving
//     means nothing is there to tell the admin a value was rejected, so an
//     empty name or a negative price is simply held back until it's valid.

/** The columns a single form field owns. Null when it maps to nothing. */
export function fieldPatch(
  field: keyof ProductFormValues,
  values: ProductFormValues,
  images: string[]
): Record<string, any> | null {
  const full = buildProductPayload(values, images) as Record<string, any>;

  switch (field) {
    // `price` and `currency` are derived from the USD price — writing
    // price_usd alone would leave the legacy fallback column stale.
    case "price_usd":
      return { price_usd: full.price_usd, price: full.price, currency: full.currency };
    // Recategorising to a tool clears ingredients, so they travel together.
    case "categories":
      return { categories: full.categories, ingredients: full.ingredients };
    default: {
      const key = field as string;
      return key in full ? { [key]: full[key] } : null;
    }
  }
}

/** False when writing this field now would store something invalid. */
export function fieldIsSaveable(
  field: keyof ProductFormValues,
  values: ProductFormValues
): boolean {
  switch (field) {
    case "name":
      return !!values.name.trim();
    case "brand":
      return !!values.brand.trim();
    case "price_gbp":
    case "price_usd": {
      const raw = values[field].trim();
      // Blank is meaningful — "not sold in this market" — so it's saveable.
      return !raw || Number(raw) > 0;
    }
    case "stock_count":
      return !values.stock_count.trim() || Number(values.stock_count) >= 0;
    case "store_link": {
      const link = values.store_link.trim();
      if (!link) return true;
      try {
        const p = new URL(link).protocol;
        return p === "http:" || p === "https:";
      } catch {
        return false;
      }
    }
    default:
      return true;
  }
}

// ── Field primitives ────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
  marginBottom: 6,
};

const hintStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: "var(--text-muted)",
  marginTop: 5,
  lineHeight: 1.45,
};

function Row({ children, cols = 2 }: { children: React.ReactNode; cols?: number }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gap: 14,
        marginBottom: 16,
      }}
    >
      {children}
    </div>
  );
}

function Text({
  label,
  value,
  onChange,
  placeholder,
  hint,
  type = "text",
  multiline,
  required,
  onBlur,
  options,
  listId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  type?: string;
  multiline?: boolean;
  required?: boolean;
  onBlur?: () => void;
  /** Suggestions offered by the browser. Does not restrict what can be typed. */
  options?: readonly string[];
  listId?: string;
}) {
  return (
    <div>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: "var(--danger)", marginLeft: 4 }}>*</span>}
      </label>
      {options && listId && (
        <datalist id={listId}>
          {options.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      )}
      {multiline ? (
        <textarea
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          rows={3}
          style={{ resize: "vertical", minHeight: 78 }}
        />
      ) : (
        <input
          className="input"
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          list={options && listId ? listId : undefined}
          {...(type === "number" ? { min: 0, step: "0.01" } : {})}
        />
      )}
      {hint && <div style={hintStyle}>{hint}</div>}
    </div>
  );
}

export function ProductFormFields({
  values,
  onChange,
  onCommit,
}: {
  values: ProductFormValues;
  onChange: (next: ProductFormValues) => void;
  /** Called when a field loses focus, for per-field auto-save when editing. */
  onCommit?: (field: keyof ProductFormValues) => void;
}) {
  const set = <K extends keyof ProductFormValues>(key: K, value: ProductFormValues[K]) =>
    onChange({ ...values, [key]: value });

  // Toggles have no blur of their own — committing on change is the
  // equivalent moment for them.
  const setAndCommit = <K extends keyof ProductFormValues>(key: K, value: ProductFormValues[K]) => {
    onChange({ ...values, [key]: value });
    onCommit?.(key);
  };
  const commit = (field: keyof ProductFormValues) => () => onCommit?.(field);

  const lowStock =
    values.in_stock && Number(values.stock_count) > 0 && Number(values.stock_count) <= 5;

  return (
    <>
      <Row>
        <Text
          label="Name"
          required
          value={values.name}
          onChange={(v) => set("name", v)}
          onBlur={commit("name")}
          placeholder="e.g. Rosemary Hair Oil"
        />
        <Text
          label="Brand"
          required
          value={values.brand}
          onChange={(v) => set("brand", v)}
          onBlur={commit("brand")}
          placeholder="e.g. Zorax"
        />
      </Row>

      <Row cols={1}>
        <Text
          label="Description"
          multiline
          value={values.description}
          onChange={(v) => set("description", v)}
          onBlur={commit("description")}
          placeholder="Short product description shown on the product page"
        />
      </Row>

      <Row cols={1}>
        <Text
          label="How to use"
          multiline
          value={values.how_to_use}
          onChange={(v) => set("how_to_use", v)}
          onBlur={commit("how_to_use")}
          placeholder="Shown as its own section on the product page. Leave blank to hide it."
        />
      </Row>

      <Row>
        <Text
          label="Price (GBP)"
          type="number"
          value={values.price_gbp}
          onChange={(v) => set("price_gbp", v)}
          onBlur={commit("price_gbp")}
          placeholder="24.99"
          hint="Blank means the product isn't sold in the UK at all."
        />
        <Text
          label="Price (USD)"
          type="number"
          value={values.price_usd}
          onChange={(v) => set("price_usd", v)}
          onBlur={commit("price_usd")}
          placeholder="29.99"
          hint="Blank means it isn't sold in the US. Prices are never converted."
        />
      </Row>

      <Row>
        <div>
          <Text
            label="Categories"
            value={values.categories}
            onChange={(v) => set("categories", v)}
            onBlur={commit("categories")}
            placeholder="Hydrate/Moisture, Detox"
            options={KNOWN_CATEGORIES}
            listId="known-categories"
            hint="Comma separated. Drives the store's category rows — spelling has to match exactly."
          />
          {/* A category the store has no tile for isn't rejected, but it does
              mean nobody browsing will ever reach this product. */}
          {unknownCategories(values.categories).length > 0 && (
            <div style={{ ...hintStyle, color: "#8a5a00", fontWeight: 700 }}>
              No store tile matches {unknownCategories(values.categories).join(", ")} — this
              product won’t appear under any category.
            </div>
          )}
        </div>
        <Text
          label="Sub-category"
          value={values.sub_category}
          onChange={(v) => set("sub_category", v)}
          onBlur={commit("sub_category")}
          placeholder="e.g. leave-in"
          hint="Used by the filter chips on the See all screen."
        />
      </Row>

      <Row>
        <Text
          label="Hair types"
          value={values.hair_types}
          onChange={(v) => set("hair_types", v)}
          onBlur={commit("hair_types")}
          placeholder="curly, coily, wavy"
          hint="Comma separated."
        />
        <Text
          label="Concerns"
          value={values.concerns}
          onChange={(v) => set("concerns", v)}
          onBlur={commit("concerns")}
          placeholder="dryness, breakage, growth"
          hint="Comma separated."
        />
      </Row>

      <Row>
        <div>
          <label style={labelStyle}>Stock count</label>
          <input
            className="input"
            type="number"
            min={0}
            step={1}
            value={values.stock_count}
            onChange={(e) => set("stock_count", e.target.value)}
            onBlur={commit("stock_count")}
            placeholder="0"
          />
          <div style={hintStyle}>
            {lowStock ? (
              <span style={{ color: "#b97800", fontWeight: 700 }}>
                At 5 or fewer the app shows a “low stock” warning.
              </span>
            ) : (
              "Decremented automatically on each sale and returned on refund."
            )}
          </div>
        </div>

        <div>
          <label style={labelStyle}>Availability</label>
          <button
            type="button"
            onClick={() => setAndCommit("in_stock", !values.in_stock)}
            className="btn"
            style={{
              width: "100%",
              justifyContent: "flex-start",
              gap: 10,
              minHeight: 42,
              background: values.in_stock ? "var(--success-light)" : "#f2f4f8",
              borderColor: values.in_stock ? "transparent" : "var(--border)",
              color: values.in_stock ? "var(--success)" : "var(--text-muted)",
            }}
            aria-pressed={values.in_stock}
          >
            <span
              style={{
                width: 34,
                height: 20,
                borderRadius: 999,
                background: values.in_stock ? "var(--success)" : "var(--border-strong)",
                position: "relative",
                flexShrink: 0,
                transition: "background 0.18s",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: 2,
                  left: values.in_stock ? 16 : 2,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: "#fff",
                  transition: "left 0.18s",
                }}
              />
            </span>
            {values.in_stock ? "Listed in the store" : "Hidden from the store"}
          </button>
          <div style={hintStyle}>
            The store only lists products with this on — independent of stock count.
          </div>
        </div>
      </Row>

      <Row cols={1}>
        <div>
          <label style={labelStyle}>Strand type</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {STRAND_TYPES.map((strand) => {
              const on = values.strand_type.includes(strand);
              return (
                <button
                  key={strand}
                  type="button"
                  className="btn"
                  onClick={() =>
                    setAndCommit(
                      "strand_type",
                      on
                        ? values.strand_type.filter((s) => s !== strand)
                        : [...values.strand_type, strand]
                    )
                  }
                  aria-pressed={on}
                  style={{
                    textTransform: "capitalize",
                    background: on ? "var(--primary-light)" : "transparent",
                    borderColor: on ? "var(--primary)" : "var(--border)",
                    color: on ? "var(--primary)" : "var(--text-muted)",
                    fontWeight: on ? 700 : 600,
                  }}
                >
                  {strand}
                </button>
              );
            })}
          </div>
          <div style={hintStyle}>
            Which strand thickness this suits. Fixed set on purpose — it&apos;s
            matched against the &ldquo;how thick is a single strand?&rdquo;
            answer from onboarding, so free text wouldn&apos;t line up.
          </div>
        </div>
      </Row>

      {/* Tools and accessories have no formulation, so the field is hidden
          rather than left blank and confusing. */}
      {hasIngredients(values.categories) && (
        <Row cols={1}>
          <Text
            label="Ingredients"
            multiline
            value={values.ingredients}
            onChange={(v) => set("ingredients", v)}
          onBlur={commit("ingredients")}
            placeholder="Water (Aqua), Cetearyl Alcohol, Behentrimonium Methosulfate…"
            hint="Full INCI list as printed on the packaging. Shown on the product page in the app and on the website."
          />
        </Row>
      )}

      <Row cols={1}>
        <Text
          label="Store link"
          value={values.store_link}
          onChange={(v) => set("store_link", v)}
          onBlur={commit("store_link")}
          placeholder="https://www.boots.com/some-product"
          hint={
            values.stock_count.trim() === "0" || !values.in_stock
              ? "This product can't be bought here right now, so the app and website will show “Visit site” in place of Add to Cart. Leave blank and they'll show a plain “Out of stock” instead."
              : "Where to send customers when this sells out. The app and website swap Add to Cart for “Visit site” whenever stock hits zero."
          }
        />
      </Row>
    </>
  );
}
